/**
 * Task Comments Redux Slice
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  TaskComment,
  CreateCommentDto,
  UpdateCommentDto,
} from '../../api/types/CommentTypes';
import { commentsApi } from '../../api/endpoints/commentsApi';

// ==================== State Interface ====================

interface CommentsState {
  // Comments by task ID
  commentsByTask: { [taskId: string]: TaskComment[] };
  commentCountsByTask: { [taskId: string]: number };
  loading: boolean;
  error: string | null;
  
  // Comment being edited
  editingCommentId: string | null;
}

const initialState: CommentsState = {
  commentsByTask: {},
  commentCountsByTask: {},
  loading: false,
  error: null,
  editingCommentId: null,
};

// ==================== Async Thunks ====================

/**
 * Fetch comments for a task
 */
export const fetchTaskComments = createAsyncThunk(
  'comments/fetchTaskComments',
  async (taskId: string) => {
    const comments = await commentsApi.getTaskComments(taskId);
    return { taskId, comments };
  },
);

/**
 * Fetch comment count for a task
 */
export const fetchCommentCount = createAsyncThunk(
  'comments/fetchCommentCount',
  async (taskId: string) => {
    const count = await commentsApi.getCommentCount(taskId);
    return { taskId, count };
  },
);

/**
 * Create a comment
 */
export const createComment = createAsyncThunk(
  'comments/createComment',
  async ({ taskId, data }: { taskId: string; data: CreateCommentDto }) => {
    const comment = await commentsApi.createComment(taskId, data);
    return { taskId, comment };
  },
);

/**
 * Update a comment
 */
export const updateComment = createAsyncThunk(
  'comments/updateComment',
  async ({
    taskId,
    commentId,
    data,
  }: {
    taskId: string;
    commentId: string;
    data: UpdateCommentDto;
  }) => {
    const comment = await commentsApi.updateComment(taskId, commentId, data);
    return { taskId, comment };
  },
);

/**
 * Delete a comment
 */
export const deleteComment = createAsyncThunk(
  'comments/deleteComment',
  async ({ taskId, commentId }: { taskId: string; commentId: string }) => {
    await commentsApi.deleteComment(taskId, commentId);
    return { taskId, commentId };
  },
);

// ==================== Slice ====================

const commentsSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    // Clear all comments
    clearComments(state) {
      state.commentsByTask = {};
      state.commentCountsByTask = {};
      state.error = null;
    },

    // Clear comments for a specific task
    clearTaskComments(state, action) {
      const taskId = action.payload;
      delete state.commentsByTask[taskId];
      delete state.commentCountsByTask[taskId];
    },

    // Set editing comment
    setEditingComment(state, action) {
      state.editingCommentId = action.payload;
    },

    // Clear errors
    clearErrors(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // ==================== Fetch Task Comments ====================
    builder
      .addCase(fetchTaskComments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTaskComments.fulfilled, (state, action) => {
        state.loading = false;
        const { taskId, comments } = action.payload;
        state.commentsByTask[taskId] = comments;
        state.commentCountsByTask[taskId] = comments.length;
      })
      .addCase(fetchTaskComments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch comments';
      });

    // ==================== Fetch Comment Count ====================
    builder
      .addCase(fetchCommentCount.fulfilled, (state, action) => {
        const { taskId, count } = action.payload;
        state.commentCountsByTask[taskId] = count;
      });

    // ==================== Create Comment ====================
    builder
      .addCase(createComment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createComment.fulfilled, (state, action) => {
        state.loading = false;
        const { taskId, comment } = action.payload;
        if (state.commentsByTask[taskId]) {
          state.commentsByTask[taskId].unshift(comment);
          state.commentCountsByTask[taskId] = state.commentsByTask[taskId].length;
        }
      })
      .addCase(createComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create comment';
      });

    // ==================== Update Comment ====================
    builder
      .addCase(updateComment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        state.loading = false;
        const { taskId, comment } = action.payload;
        if (state.commentsByTask[taskId]) {
          const index = state.commentsByTask[taskId].findIndex(
            (c) => c.id === comment.id,
          );
          if (index !== -1) {
            state.commentsByTask[taskId][index] = comment;
          }
        }
        state.editingCommentId = null;
      })
      .addCase(updateComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update comment';
      });

    // ==================== Delete Comment ====================
    builder
      .addCase(deleteComment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.loading = false;
        const { taskId, commentId } = action.payload;
        if (state.commentsByTask[taskId]) {
          state.commentsByTask[taskId] = state.commentsByTask[taskId].filter(
            (c) => c.id !== commentId,
          );
          state.commentCountsByTask[taskId] = state.commentsByTask[taskId].length;
        }
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete comment';
      });
  },
});

// ==================== Exports ====================

export const {
  clearComments,
  clearTaskComments,
  setEditingComment,
  clearErrors,
} = commentsSlice.actions;

export default commentsSlice.reducer;
